import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import SupportTicketView, { handleSupportActionError } from '../../components/support/v3/SupportTicketView';
import ContextPanel from '../../components/support/v3/ContextPanel';
import type { ComposeTarget } from '../../components/support/constants';
import { getMyProfile, removeRepresentativeToken } from '../../services/representativeService';
import useSupportSocket, { PresenceViewer, TypingEvent } from '../../hooks/useSupportSocket';
import representativeSupportApi from '../../services/adapters/representativeSupportApi';
import { representativeKnowledgeApi } from '../../services/supportKnowledgeApi';
// Token `--a2-*` (dichiarati su `.adm2, .sup3`) e classi `.s3-*`: il contenitore
// V3 porta la sola classe `.sup3`, mai `.adm2`.
import '../../styles/admin-v2.css';
import '../../styles/support-v3.css';
import { SupportLookProvider, supportRootClass } from '../../components/support/v4/lookContext';
import RepTitleBar, {
  REP_CRUMBS,
  RepNotice,
  RepState,
  repTools,
  useCanManageChannels,
  type RepKpiNavState,
} from '../../components/support/v4/RepTitleBar';
import QuickTransferSheet, { type QuickTransferApi } from '../../components/support/v4/QuickTransferSheet';
import { transferArgs } from '../../components/support/copyGroupsModel';
import { buildKpis, type S3Kpi } from '../../components/support/v3/kpiModel';
import { REALTIME_THROTTLE_MS, throttleDelay } from '../../components/support/v4/dispatchModel';
import type {
  Candidate,
  GroupLite,
  Macro,
  StatsData,
  TicketDetail as TicketDetailData,
} from '../../services/supportApi';

const LOST_ACCESS_TITLE = 'Non hai più accesso a questo ticket';
const LOST_ACCESS_TEXT = 'È stato trasferito a un canale di cui non sei componente.';

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

// Contenitore del dettaglio ticket per il rappresentante: profilo, caricamento
// dati, realtime (eventi, presence, typing). La UI sta in `SupportTicketView`
// (V3), col pannello Contesto SEMPRE nel cassetto "Dettagli": `.assoc-app .page`
// e largo al massimo 1180px, le tre colonne non ci starebbero (ruling 13).
const AssociativeSupportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const api = representativeSupportApi;
  // Come in TicketQueue: l'identita di `api` non e garantita stabile, quindi gli
  // effetti leggono apiRef.current e non hanno `api` fra le dipendenze.
  const apiRef = useRef(api);
  apiRef.current = api;

  const [profile, setProfile] = useState<RepProfile | null>(null);
  const canLive = profile?.permissions?.some((p) => p.name === 'associativa.live.publish') ?? false;
  // Il trasferimento ad altro canale ha un permesso suo: finché il profilo non è
  // arrivato si resta sul default (true), come prima, per non far "lampeggiare"
  // il bottone; il backend resta comunque l'unico gate autorevole.
  const canTransfer = profile
    ? (profile.permissions?.some((p) => p.name === 'associativa.supporto.transfer') ?? false)
    : true;
  // Modifica dei messaggi del thread pubblico: due permessi distinti (richiedente
  // e canale). Qui il default e false anche prima che il profilo arrivi: la matita
  // non deve comparire a chi non ha il permesso, nemmeno per un istante.
  const hasAssocPermission = (name: string) =>
    profile?.permissions?.some((p) => p.name === name) ?? false;
  const canEditRequesterMessages = hasAssocPermission('associativa.supporto.edit_requester_messages');
  const canEditChannelMessages = hasAssocPermission('associativa.supporto.edit_channel_messages');
  // Pannello Contesto (Proprietà: select «Canale» e sezione «In copia» con
  // Aggiungi…, X e «Rendi responsabile»): false finché il profilo non arriva,
  // così chi non ha il permesso non vede quei comandi nemmeno per un istante.
  // Il bottone del dettaglio resta su `canTransfer` (default true, senza lampeggio).
  const canTransferInPanel = hasAssocPermission('associativa.supporto.transfer');
  const canClose = hasAssocPermission('associativa.supporto.close');
  const canChangeRequester = hasAssocPermission('associativa.supporto.change_requester');
  // Con il permesso di lettura un 403 sul singolo ticket non e «carica senza
  // permesso»: il ticket e uscito dal perimetro (trasferito ad altro canale).
  const canRead = hasAssocPermission('associativa.supporto.read');
  // Base di conoscenza (Task 29): link ai casi simili e «Crea scheda da questo ticket».
  const knowledgeRead = hasAssocPermission('associativa.supporto.knowledge_read');
  const knowledgeManage = hasAssocPermission('associativa.supporto.knowledge_manage');
  // Redesign v4 (T32): pelle opt-in col permesso di anteprima (v3 finche il profilo non arriva).
  const look = hasAssocPermission('associativa.supporto.v4_preview') ? 'v4' : 'v3';
  const v4 = look === 'v4';
  const knowledge = React.useMemo(
    () => ({ api: representativeKnowledgeApi, canRead: knowledgeRead, canManage: knowledgeManage }),
    [knowledgeRead, knowledgeManage]
  );

  const [forbidden, setForbidden] = useState(false);
  // Barra v4 (T41, N22): stessi contatori della coda; una pillola riporta alla
  // coda col filtro (`location.state.kpiFilter`), perche il dettaglio ha una rotta sua (RA-08).
  const [stats, setStats] = useState<StatsData | null>(null);
  const loadStats = useCallback(() => {
    apiRef.current
      .getStats()
      .then(setStats)
      .catch(() => {
        /* la barra resta senza pillole */
      });
  }, []);
  const loadStatsRef = useRef<(() => void) | null>(null);
  loadStatsRef.current = v4 ? loadStats : null;
  // Tempo reale: le statistiche si ricaricano al massimo una volta al secondo,
  // con una ricarica in coda per l'ultimo evento (come la coda, CT-44).
  const statsLastRef = useRef(0);
  const statsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestStats = useCallback(() => {
    const run = () => {
      statsLastRef.current = Date.now();
      loadStatsRef.current?.();
    };
    const delay = throttleDelay(statsLastRef.current, Date.now(), REALTIME_THROTTLE_MS);
    if (delay === 0) run();
    else if (!statsTimerRef.current) {
      statsTimerRef.current = setTimeout(() => {
        statsTimerRef.current = null;
        run();
      }, delay);
    }
  }, []);
  useEffect(() => {
    if (!v4) return;
    statsLastRef.current = Date.now();
    loadStats();
  }, [v4, loadStats]);
  const pickKpi = (k: S3Kpi) => {
    if (!k.filter) return;
    const state: RepKpiNavState = {
      kpiFilter: {
        status: k.filter.status,
        unread: k.filter.unread,
        // «BOZZE» (Task 46): il filtro rapido viaggia con la pillola.
        ...(k.filter.quick ? { quick: k.filter.quick } : {}),
      },
    };
    navigate('/area-associativa/supporto', { state });
  };
  // Responsabile di canale (Task 46): ingranaggio verso le impostazioni dei canali.
  const canManageChannels = useCanManageChannels(api, hasAssocPermission('associativa.supporto.manage_channel'));
  const [ticket, setTicket] = useState<TicketDetailData | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [allGroups, setAllGroups] = useState<GroupLite[]>([]);
  const [macros, setMacros] = useState<Macro[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [viewers, setViewers] = useState<PresenceViewer[]>([]);
  const [typingName, setTypingName] = useState<string | null>(null);
  const typingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const replyTypingActiveRef = useRef<boolean>(false);
  const noteTypingActiveRef = useRef<boolean>(false);
  const replyTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noteTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cassetto "Dettagli": lo stato vive qui perche il cassetto lo rende
  // ContextPanel (open + onClose), non SupportTicketView. `ctxFocus` dice su
  // quale card aprirlo (da "Cambia stato / priorità" sono le Proprieta).
  const [ctxOpen, setCtxOpen] = useState<boolean>(false);
  const [ctxFocus, setCtxFocus] = useState<'properties' | undefined>(undefined);
  // Trasferimento chiesto dal pannello Proprieta (canale gia scelto): il nonce
  // riapre la modale anche sulla stessa destinazione.
  const transferNonceRef = useRef(0);
  const [transferRequest, setTransferRequest] = useState<{ groupId: string; nonce: number } | undefined>(undefined);
  // v4 (T45): pannello rapido al posto di TransferModal, aperto dal menu ⋯ e dal
  // «Trasferisci» di Proprieta (`onOpenTransfer` di SupportTicketView). v3 = modale.
  const [quickTransfer, setQuickTransfer] = useState<{ initialGroupId: string } | null>(null);

  // ===== Profilo (pattern comune area associativa) =====
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Trasferimento appena riuscito (o in volo): se il ticket e uscito dal
  // perimetro del rappresentante la ricarica risponde 403/404, che NON va letto
  // come «carica senza permesso». Lo alza l'adapter della pagina (sotto) prima
  // della POST, cosi anche la ricarica del tempo reale che arriva in mezzo tace.
  const transferringRef = useRef(false);

  const loadTicket = useCallback(async (): Promise<'ok' | 'gone' | 'error'> => {
    if (!id) return 'error';
    try {
      const [t, c] = await Promise.all([
        apiRef.current.getTicket(id),
        apiRef.current.getCandidates(id),
      ]);
      setTicket(t);
      setCandidates(c);
      return 'ok';
    } catch (err: any) {
      const status = err.response?.status;
      // Ticket trasferito fuori dal perimetro: ci pensa `finishTransfer`.
      if (transferringRef.current && (status === 403 || status === 404)) return 'gone';
      // 403 = carica senza il permesso Supporto (o ticket fuori perimetro, vedi
      // il render), non sessione scaduta: niente login.
      if (status === 403) {
        setForbidden(true);
        return 'error';
      }
      if (status === 401) {
        navigate('/accesso-associati');
        return 'error';
      }
      toast.error('Errore caricamento ticket');
      return 'error';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);
  const reload = useCallback(async () => {
    await loadTicket();
  }, [loadTicket]);

  // Adapter della pagina: quello vero, con `transfer` che segnala il trasferimento in corso.
  const pageApi = React.useMemo(
    () => ({
      ...api,
      transfer: async (tid: string, toGroupId: string, motivo: string, copyGroupIds?: string[]) => {
        transferringRef.current = true;
        try {
          await apiRef.current.transfer(tid, toGroupId, motivo, ...transferArgs(copyGroupIds));
        } catch (err) {
          transferringRef.current = false;
          throw err;
        }
      },
    }),
    [api]
  );

  // Dopo un trasferimento riuscito (modale v3, pannello rapido v4, Proprieta,
  // menu ⋯): se il ticket non e piu visibile si torna alla coda con conferma,
  // mai «Funzione non abilitata».
  const allGroupsRef = useRef<GroupLite[]>([]);
  allGroupsRef.current = allGroups;
  const finishTransfer = useCallback(
    async (toGroupId: string) => {
      requestStats();
      const outcome = await loadTicket();
      transferringRef.current = false;
      if (outcome === 'gone') {
        const nome = allGroupsRef.current.find((g) => g._id === toGroupId)?.nome;
        toast.success(nome ? `Ticket trasferito a ${nome}` : 'Ticket trasferito');
        navigate('/area-associativa/supporto');
        return;
      }
      toast.success('Ticket trasferito');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadTicket, requestStats, navigate]
  );

  // Errori delle card del pannello Contesto (il 409 lo gestiscono gia loro).
  const onContextActionError = useCallback(
    (err: unknown) => {
      void handleSupportActionError(err, 'Errore aggiornamento ticket', toast, reload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reload]
  );

  // API del pannello rapido. L'anteprima e il ticket GIA caricato qui (letto da
  // ref, nessuna chiamata: il backend rappresentante non ha `markRead=0` e la
  // scheda e comunque gia aperta). Niente `getDispatchSummary`: la rotta non
  // esiste lato rappresentante e il pannello mostra solo i nomi dei canali.
  const ticketRef = useRef<TicketDetailData | null>(null);
  ticketRef.current = ticket;
  const quickApi = React.useMemo<QuickTransferApi>(
    () => ({
      transfer: (tid, toGroupId, motivo, copyGroupIds) => pageApi.transfer(tid, toGroupId, motivo, ...transferArgs(copyGroupIds)),
      getTicketPreview: async () => {
        if (!ticketRef.current) throw new Error('ticket non caricato');
        return ticketRef.current;
      },
    }),
    [pageApi]
  );
  const openQuickTransfer = useCallback((toGroupId: string) => {
    setQuickTransfer({ initialGroupId: toGroupId });
  }, []);

  const openTransferModal = useCallback((toGroupId: string) => {
    transferNonceRef.current += 1;
    setTransferRequest({ groupId: toGroupId, nonce: transferNonceRef.current });
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await reload();
      try {
        const g = await apiRef.current.getGroups();
        setAllGroups(g.all || []);
      } catch {
        /* la select trasferimento resta vuota */
      }
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  // Realtime: ricarica il ticket ad ogni evento sul thread/note + collision detection
  const { emitTyping } = useSupportSocket({
    area: 'representative',
    ticketId: id,
    onTicketEvent: () => {
      reload();
      requestStats();
    },
    // Parte B: tolto dai partecipanti, il ticket non e piu visibile. Si torna
    // alla coda: lo smontaggio esegue leave_ticket (useSupportSocket).
    onGroupEvent: (e) => {
      if (e.type !== 'uninvited' || String(e.ticketId) !== String(id)) return;
      toast.warning('Sei stato rimosso da questo ticket');
      navigate('/area-associativa/supporto');
    },
    onPresence: (e) => setViewers(e.viewers),
    onTyping: (e: TypingEvent) => {
      // Ignora i propri eventi typing
      if (profile && e.repId === profile._id) return;
      if (e.isTyping) {
        setTypingName(e.name);
        if (typingClearRef.current) clearTimeout(typingClearRef.current);
        typingClearRef.current = setTimeout(() => setTypingName(null), 3000);
      } else {
        setTypingName(null);
        if (typingClearRef.current) clearTimeout(typingClearRef.current);
      }
    },
  });

  // Macro del canale del ticket (ricarica solo al cambio canale, non ad ogni evento realtime)
  const ticketGroupId = ticket?.groupId?._id;
  useEffect(() => {
    apiRef.current
      .getMacros(ticketGroupId)
      .then(setMacros)
      .catch(() => {
        /* il menu risposte rapide resta vuoto */
      });
  }, [ticketGroupId]);

  // Pulizia timer typing allo smontaggio
  useEffect(
    () => () => {
      if (typingClearRef.current) clearTimeout(typingClearRef.current);
      if (replyTypingTimerRef.current) clearTimeout(replyTypingTimerRef.current);
      if (noteTypingTimerRef.current) clearTimeout(noteTypingTimerRef.current);
      if (statsTimerRef.current) clearTimeout(statsTimerRef.current);
    },
    []
  );

  // Indicatore "sta scrivendo": emette true alla prima battuta, false dopo ~1s di inattività
  const signalTyping = (target: ComposeTarget) => {
    if (!id) return;
    const activeRef = target === 'reply' ? replyTypingActiveRef : noteTypingActiveRef;
    const timerRef = target === 'reply' ? replyTypingTimerRef : noteTypingTimerRef;
    if (!activeRef.current) {
      activeRef.current = true;
      emitTyping(id, target, true);
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      activeRef.current = false;
      emitTyping(id, target, false);
    }, 1000);
  };

  // Ferma subito l'indicatore "sta scrivendo" (dopo invio): emette typing:false.
  const stopTyping = (target: ComposeTarget) => {
    if (!id) return;
    const activeRef = target === 'reply' ? replyTypingActiveRef : noteTypingActiveRef;
    const timerRef = target === 'reply' ? replyTypingTimerRef : noteTypingTimerRef;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (activeRef.current) { activeRef.current = false; emitTyping(id, target, false); }
  };

  const shellProps = {
    route: 'supporto' as const,
    nome: profile?.nome,
    cognome: profile?.cognome,
    caricaPrincipale: profile?.caricaPrincipale,
    tipo: profile?.tipo,
    foto: profile?.foto,
    associatesAccess: profile?.associatesAccess,
    canLive,
  };

  const bar = (
    <RepTitleBar
      crumbs={[REP_CRUMBS[0], { label: 'Supporto', to: '/area-associativa/supporto' }]}
      title="Ticket di supporto"
      kpis={buildKpis(stats).filter((k) => k.key !== 'dispatch')}
      // Nel dettaglio nessuna pillola e «accesa»: lo stato fittizio non combacia con nessun filtro.
      current={{ status: '__nessuno__', unread: false }}
      onPickKpi={pickKpi}
      tools={repTools(knowledgeRead, canManageChannels)}
    />
  );

  // Stati della pagina con la pelle v4 (RA-12): testi e comportamenti invariati.
  if (v4 && (loading || forbidden || !ticket)) {
    return (
      <SupportLookProvider look={look}>
      <AssociativeAppShell {...shellProps}>
        <div className="page fade-in">
          {loading ? (
            <RepState loading>Caricamento…</RepState>
          ) : forbidden && canRead ? (
            <RepNotice title={LOST_ACCESS_TITLE}>
              {LOST_ACCESS_TEXT}{' '}
              <Link to="/area-associativa/supporto">Torna alla coda</Link>
            </RepNotice>
          ) : forbidden ? (
            <RepNotice title="Funzione non abilitata per la tua carica">
              La sezione Supporto non è attiva per il tuo ruolo associativo. Se pensi che ti serva, chiedi alla segreteria nazionale di abilitarla dai permessi associativi.
            </RepNotice>
          ) : (
            <>
              {bar}
              <RepState>
                <p style={{ margin: 0 }}>Ticket non trovato.</p>
                <button
                  type="button"
                  onClick={() => navigate('/area-associativa/supporto')}
                  className="s4-repstate__back"
                >
                  Torna alla coda
                </button>
              </RepState>
            </>
          )}
        </div>
      </AssociativeAppShell>
      </SupportLookProvider>
    );
  }

  if (loading) {
    return (
      <AssociativeAppShell {...shellProps}>
        <div className="flex items-center justify-center p-12">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (forbidden && canRead) {
    return (
      <AssociativeAppShell {...shellProps}>
        <div className="page fade-in">
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5 max-w-xl">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{LOST_ACCESS_TITLE}</p>
            <p className="text-sm text-amber-900/80 dark:text-amber-200/80 mt-1">{LOST_ACCESS_TEXT}</p>
            <Link to="/area-associativa/supporto" className="inline-block mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Torna alla coda
            </Link>
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (forbidden) {
    return (
      <AssociativeAppShell {...shellProps}>
        <div className="page fade-in">
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5 max-w-xl">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Funzione non abilitata per la tua carica</p>
            <p className="text-sm text-amber-900/80 dark:text-amber-200/80 mt-1">
              La sezione Supporto non è attiva per il tuo ruolo associativo. Se pensi che ti serva, chiedi alla segreteria nazionale di abilitarla dai permessi associativi.
            </p>
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (!ticket) {
    return (
      <AssociativeAppShell {...shellProps}>
        <div className="flex flex-col items-center justify-center gap-3 p-12">
          <p className="text-sm text-gray-500 dark:text-gray-400">Ticket non trovato.</p>
          <button
            type="button"
            onClick={() => navigate('/area-associativa/supporto')}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Torna alla coda
          </button>
        </div>
      </AssociativeAppShell>
    );
  }

  return (
    <SupportLookProvider look={look}>
    <AssociativeAppShell {...shellProps}>
      <div className="page fade-in">
        {v4 && bar}
        <div className={supportRootClass('sup3', look)}>
          <SupportTicketView
            api={pageApi}
            ticket={ticket}
            candidates={candidates}
            allGroups={allGroups}
            macros={macros}
            operatorName={profile ? `${profile.nome} ${profile.cognome}` : ''}
            viewers={viewers}
            selfId={profile?._id}
            typingName={typingName}
            onTypingSignal={signalTyping}
            onTypingStop={stopTyping}
            canTransfer={canTransfer}
            canEditRequesterMessages={canEditRequesterMessages}
            canEditChannelMessages={canEditChannelMessages}
            canClose={canClose}
            canChangeRequester={canChangeRequester}
            knowledge={knowledge}
            onChanged={reload}
            onTransferred={finishTransfer}
            onNavigate={(path) => navigate(path)}
            onBack={() => navigate('/area-associativa/supporto')}
            contextMode="drawer"
            onContextOpen={(card) => {
              setCtxFocus(card);
              setCtxOpen(true);
            }}
            transferRequest={transferRequest}
            onOpenTransfer={v4 ? openQuickTransfer : undefined}
            noteAuthorFallback="Rappresentante"
            contextSlot={
              <ContextPanel
                ticket={ticket}
                api={api}
                candidates={candidates}
                groups={allGroups}
                canHandle
                canTransfer={canTransferInPanel}
                showRequesterCard={false}
                contextMode="drawer"
                open={ctxOpen}
                onClose={() => setCtxOpen(false)}
                onChanged={reload}
                onOpenTransfer={openTransferModal}
                onActionError={onContextActionError}
                onOpenTicket={(tid) => navigate(api.ticketPath(tid))}
                initialFocusCard={ctxFocus}
              />
            }
          />
        </div>
        {v4 && quickTransfer && (
          <QuickTransferSheet
            ticket={ticket}
            groups={allGroups}
            api={quickApi}
            initialGroupId={quickTransfer.initialGroupId}
            onClose={() => setQuickTransfer(null)}
            announce={false}
            onTransferred={(_tid, toGroupId) => finishTransfer(toGroupId)}
            onChanged={reload}
            allowCopies
          />
        )}
      </div>
    </AssociativeAppShell>
    </SupportLookProvider>
  );
};

export default AssociativeSupportDetail;
